// Port of new arty's `components/arty/community/ask-kinds.ts` plus the payload rules inside
// `AskForms.tsx` and `use-ask-submit.ts` (arty repo), pulled out framework-free so
// `api/test/client/community-ask.spec.ts` can prove them. Copy lives in the `arty-card`
// catalog under `community-ask`, not here.
import type { CommunityKind } from '~/lib/arty-actions/catalog';

export type AskPostType = 'CRITIQUE' | 'ART_RATING' | 'PRICING_FEEDBACK' | 'POLL';

export const POST_TYPES: Record<CommunityKind, AskPostType> = {
  critique: 'CRITIQUE',
  rating: 'ART_RATING',
  pricing: 'PRICING_FEEDBACK',
  poll: 'POLL',
};

/** Windows arthelper accepts, per ask. A critique is 7 or 30 only. */
export const WINDOW_DAYS: Record<CommunityKind, number[]> = {
  critique: [7, 30],
  rating: [3, 7, 30],
  pricing: [3, 7, 30],
  poll: [3, 7, 30],
};

/** Focus areas are a closed set server-side; anything else is rejected. */
export const CRITIQUE_FOCUS_IDS = ['composition', 'color-value', 'technique', 'concept', 'marketability', 'pricing'];

/**
 * arthelper stores experience and destination as free text, so the value sent is
 * the English phrase new arty sends (posts read the same whichever app wrote them);
 * the key only picks the translated label.
 */
export const EXPERIENCE: Record<string, string> = {
  starting: 'Just starting out',
  'few-years': 'A few years in',
  seasoned: 'Seasoned',
  professional: 'Professional',
};

export const DESTINATION: Record<string, string> = {
  practice: 'Practice piece',
  portfolio: 'Portfolio',
  shop: 'Shop or for sale',
  show: 'Show or gallery',
};

/** arthelper rejects an intent under 100 characters, so the form says so first. */
export const INTENT_MIN_CHARS = 100;
export const POLL_OPTION_MIN = 2;
export const POLL_OPTION_MAX = 4;
export const POLL_LABEL_MAX = 80;
const PRICE_MIN_SPREAD_CENTS = 100;

/** Everything a form contributes to the post; `postInput` supplies the rest. */
export type AskDraft = {
  title?: string;
  content?: string;
  critique?: any;
  poll?: any;
  pricing?: any;
  rating?: any;
};

/**
 * `Number("")` is 0, which is how a blank "Lowest price" becomes a real $0 floor.
 * Empty is rejected before anything parses, and the format is checked, not coerced.
 */
export function dollarsToCents(value: string): number | null {
  const cleaned = value.replace(/[$,\s]/g, '');
  if (!cleaned) {
    return null;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return null;
  }
  const cents = Math.round(Number(cleaned) * 100);
  if (!Number.isSafeInteger(cents)) {
    return null;
  }
  return cents;
}

export function ratingDraft(durationDays: number): AskDraft {
  return { rating: { durationDays } };
}

/** Null until both ends parse and the high end clears the low by at least $1. */
export function pricingDraft(low: string, high: string, durationDays: number): AskDraft | null {
  const minValueCents = dollarsToCents(low);
  const maxValueCents = dollarsToCents(high);
  if (minValueCents === null || maxValueCents === null) {
    return null;
  }
  if (maxValueCents - minValueCents < PRICE_MIN_SPREAD_CENTS) {
    return null;
  }
  return { pricing: { minValueCents, maxValueCents, durationDays } };
}

/** Null until there is a question and every option row (at least two) has a label. */
export function pollDraft(question: string, options: string[], durationDays: number): AskDraft | null {
  const content = question.trim();
  const labels = options.map((option) => option.trim());
  if (!content || labels.length < POLL_OPTION_MIN || labels.length > POLL_OPTION_MAX) {
    return null;
  }
  if (labels.some((label) => !label)) {
    return null;
  }
  return {
    content,
    poll: { options: labels.map((label) => ({ label })), durationDays },
  };
}

export type CritiqueFields = {
  title: string;
  medium: string;
  dimensions: string;
  stage: 'WIP' | 'FINISHED';
  intent: string;
  focusAreas: string[];
  experience: string;
  destination: string;
  canPaintOver: boolean;
  windowDays: number;
};

/** Null until the title, medium, a 100-character intent and one focus area are in. */
export function critiqueDraft(fields: CritiqueFields): AskDraft | null {
  const title = fields.title.trim();
  const medium = fields.medium.trim();
  const intent = fields.intent.trim();
  if (!title || !medium || intent.length < INTENT_MIN_CHARS || fields.focusAreas.length === 0) {
    return null;
  }
  return {
    title,
    critique: {
      intent,
      focusAreas: fields.focusAreas,
      windowDays: fields.windowDays,
      stage: fields.stage,
      medium,
      dimensions: fields.dimensions.trim() || null,
      experience: EXPERIENCE[fields.experience] ?? null,
      destination: DESTINATION[fields.destination] ?? null,
      canPaintOver: fields.canPaintOver,
    },
  };
}

/**
 * The `CreateCommunityPostInput` for one ask. The piece rides as its social copy,
 * the only image, so it is the main image ("main image first" is the API's contract).
 */
export function postInput(kind: CommunityKind, communityId: string, socialImageId: string, draft: AskDraft) {
  return {
    communityId,
    type: POST_TYPES[kind],
    socialImageIds: [socialImageId],
    ...draft,
  };
}
